// src/components/AddAddressDialog.tsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { CustomerAddress } from '../types';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface AddAddressDialogProps {
    customerId: string;
    onClose: () => void;
    onAddressAdded: (address: CustomerAddress) => void;
}

export const AddAddressDialog: React.FC<AddAddressDialogProps> = ({
                                                                      customerId,
                                                                      onClose,
                                                                      onAddressAdded
                                                                  }) => {
    const [countries, setCountries] = useState<any[]>([]);
    const [regions, setRegions] = useState<any[]>([]);
    const [cities, setCities] = useState<any[]>([]);
    const [formData, setFormData] = useState({
        country_code: '',
        region: '',
        city: '',
        street: '',
        house: '',
        no_house: false,  // ✅ Добавляем флаг "нет номера дома"
        apartment: '',
        postal_code: ''
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [loadingRegions, setLoadingRegions] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    // Загрузка стран
    useEffect(() => {
        const loadCountries = async () => {
            const { data } = await supabase
                .from('countries')
                .select('*')
                .eq('is_active', true)
                .order('sort_order');
            setCountries(data || []);
        };
        loadCountries();
    }, []);

    // Загрузка регионов при выборе страны
    useEffect(() => {
        if (!formData.country_code) {
            setRegions([]);
            setCities([]);
            return;
        }

        const loadRegions = async () => {
            setLoadingRegions(true);
            try {
                const { data: country } = await supabase
                    .from('countries')
                    .select('id')
                    .eq('code', formData.country_code)
                    .maybeSingle();

                if (country) {
                    const { data } = await supabase
                        .from('regions')
                        .select('*')
                        .eq('country_id', country.id)
                        .eq('is_active', true)
                        .order('sort_order');
                    setRegions(data || []);
                } else {
                    setRegions([]);
                }
            } catch (error) {
                console.error('Error loading regions:', error);
                setRegions([]);
            } finally {
                setLoadingRegions(false);
            }
        };
        loadRegions();
    }, [formData.country_code]);

    // Загрузка городов при выборе региона
    useEffect(() => {
        if (!formData.region) {
            setCities([]);
            return;
        }

        const loadCities = async () => {
            setLoadingCities(true);
            try {
                const { data: region, error: regionError } = await supabase
                    .from('regions')
                    .select('id')
                    .eq('name_ru', formData.region)
                    .maybeSingle();

                if (regionError) {
                    console.error('Error finding region:', regionError);
                    setCities([]);
                    return;
                }

                if (region) {
                    const { data, error: citiesError } = await supabase
                        .from('cities')
                        .select('*')
                        .eq('region_id', region.id)
                        .eq('is_active', true)
                        .order('sort_order');

                    if (citiesError) {
                        console.error('Error loading cities:', citiesError);
                        setCities([]);
                    } else {
                        setCities(data || []);

                        if (data && data.length === 1) {
                            setFormData(prev => ({
                                ...prev,
                                city: data[0].name_ru
                            }));
                            if (errors.city) {
                                setErrors(prev => ({ ...prev, city: '' }));
                            }
                        }
                    }
                } else {
                    setCities([]);
                }
            } catch (error) {
                console.error('Error loading cities:', error);
                setCities([]);
            } finally {
                setLoadingCities(false);
            }
        };

        loadCities();
    }, [formData.region]);

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.country_code) newErrors.country_code = 'Выберите страну';
        if (!formData.city) newErrors.city = 'Выберите город';
        if (!formData.street) newErrors.street = 'Введите улицу';
        // ✅ Номер дома теперь не обязателен, если стоит галочка
        if (!formData.no_house && !formData.house) {
            newErrors.house = 'Введите номер дома или отметьте "Нет номера"';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setSubmitting(true);
        try {
            // ✅ Если нет номера дома, сохраняем пустую строку '' вместо null
            const houseValue = formData.no_house ? '' : (formData.house || '');

            console.log('Saving address with house value:', houseValue); // Для отладки

            const { data, error } = await supabase
                .from('customer_addresses')
                .insert({
                    customer_id: customerId,
                    country_code: formData.country_code,
                    region: formData.region || null,
                    city: formData.city,
                    street: formData.street,
                    house: houseValue,  // ✅ Пустая строка вместо null
                    apartment: formData.apartment || null,
                    postal_code: formData.postal_code || null,
                    is_default: false
                })
                .select()
                .single();

            if (error) {
                console.error('supabase error details:', error);
                throw error;
            }

            toast.success('Адрес добавлен');
            onAddressAdded(data);
            onClose();
        } catch (error: any) {
            console.error('Error adding address:', error);
            toast.error(error.message || 'Ошибка добавления адреса');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Добавить новый адрес</h3>
                    <button className="modal-close" onClick={onClose}>
                        <XMarkIcon style={{ width: 20, height: 20 }} />
                    </button>
                </div>
                <div className="modal-body">
                    <div className="form-group">
                        <label>Страна *</label>
                        <select
                            value={formData.country_code}
                            onChange={(e) => setFormData({
                                ...formData,
                                country_code: e.target.value,
                                region: '',
                                city: ''
                            })}
                            className={errors.country_code ? 'error' : ''}
                        >
                            <option value="">Выберите страну</option>
                            {countries.map(c => (
                                <option key={c.code} value={c.code}>{c.name_ru}</option>
                            ))}
                        </select>
                        {errors.country_code && <span className="error-text">{errors.country_code}</span>}
                    </div>

                    <div className="form-group">
                        <label>Регион/Область</label>
                        <select
                            value={formData.region}
                            onChange={(e) => setFormData({ ...formData, region: e.target.value, city: '' })}
                            disabled={!formData.country_code || loadingRegions}
                        >
                            <option value="">{loadingRegions ? 'Загрузка...' : 'Выберите регион'}</option>
                            {regions.map(r => (
                                <option key={r.id} value={r.name_ru}>{r.name_ru}</option>
                            ))}
                        </select>
                        {loadingRegions && <span className="loading-text">Загрузка регионов...</span>}
                    </div>

                    <div className="form-group">
                        <label>Город/Район *</label>
                        <select
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            className={errors.city ? 'error' : ''}
                            disabled={!formData.region || loadingCities}
                        >
                            <option value="">
                                {!formData.region
                                    ? 'Сначала выберите регион'
                                    : loadingCities
                                        ? 'Загрузка городов...'
                                        : cities.length === 0
                                            ? 'Нет городов в регионе'
                                            : 'Выберите город'}
                            </option>
                            {cities.map(c => (
                                <option key={c.id} value={c.name_ru}>
                                    {c.name_ru}
                                    {c.is_capital && ' (центр)'}
                                </option>
                            ))}
                        </select>
                        {errors.city && <span className="error-text">{errors.city}</span>}
                        {loadingCities && <span className="loading-text">Загрузка городов...</span>}
                        {!loadingCities && formData.region && cities.length === 0 && (
                            <span className="error-text">Нет городов в выбранном регионе</span>
                        )}
                        {!loadingCities && formData.region && cities.length === 1 && (
                            <span className="hint-text">Город автоматически выбран</span>
                        )}
                    </div>

                    <div className="form-group">
                        <label>Улица *</label>
                        <input
                            type="text"
                            value={formData.street}
                            onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                            className={errors.street ? 'error' : ''}
                            placeholder="Название улицы"
                        />
                        {errors.street && <span className="error-text">{errors.street}</span>}
                    </div>

                    <div className="form-row">
                        <div className="form-group input_house" style={{ flex: 1 }}>
                            <label>Дом {!formData.no_house && '*'}</label>
                            <input
                                type="text"
                                value={formData.house}
                                onChange={(e) => setFormData({ ...formData, house: e.target.value, no_house: false })}
                                className={errors.house ? 'error' : ''}
                                placeholder="№ дома"
                                disabled={formData.no_house}
                            />
                        </div>


                            <label style={{ opacity: 0 }}>.</label>
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={formData.no_house}
                                    onChange={(e) => {
                                        setFormData({
                                            ...formData,
                                            no_house: e.target.checked,
                                            house: e.target.checked ? '' : formData.house
                                        });
                                        if (e.target.checked && errors.house) {
                                            setErrors(prev => ({ ...prev, house: '' }));
                                        }
                                    }}
                                />
                                <span>Нет номера дома</span>
                            </label>


                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Квартира</label>
                            <input
                                type="text"
                                value={formData.apartment}
                                onChange={(e) => setFormData({ ...formData, apartment: e.target.value })}
                                placeholder="№ квартиры"
                            />
                        </div>
                        <div className="form-group">
                            <label>Почтовый индекс</label>
                            <input
                                type="text"
                                value={formData.postal_code}
                                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                                placeholder="Индекс"
                            />
                        </div>
                    </div>

                    {errors.house && <span className="error-text">{errors.house}</span>}
                </div>
                <div className="modal-footer">
                    <button className="cancel-btn" onClick={onClose}>Отмена</button>
                    <button className="save-btn" onClick={handleSave} disabled={submitting}>
                        {submitting ? 'Сохранение...' : 'Добавить адрес'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddAddressDialog;